import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { record } from '@rrweb/record';
import type { eventWithTime } from '@rrweb/types';
import { EventType } from '@rrweb/types';
import { AppAuthService, AuthUser } from '../auth/app-auth.service';
import { buildRecordOptions } from './ux-masking';
import { templatePath } from './ux-url';
import { gitVersion } from '../../../version';

const BLOCKED_PREFIXES = ['/login', '/get-started', '/reset', '/register', '/invite', '/newsletter', '/admin'];
const FLUSH_MS = 10_000;
const FLUSH_BYTES = 256 * 1024;
const BUFFER_LIMIT = 2 * 1024 * 1024;
const FINAL_CHUNK_LIMIT = 60 * 1024; // fetch keepalive budget is 64 KiB
const MAX_FAILURES = 10;
const SESSION_MAX_MS = 4 * 3600_000;

@Injectable({ providedIn: 'root' })
export class UxRecorderService {
  private readonly auth = inject(AppAuthService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  active = false;
  sessionId?: string;
  private stopFn?: () => void;
  private buffer: eventWithTime[] = [];
  private bufferBytes = 0;
  private seq = 0;
  private startedAt = 0;
  private failures = 0;
  private timer?: ReturnType<typeof setInterval>;
  private flushing = false;
  private finishing = false;
  /** Set on a 4xx chunk response or MAX_FAILURES exhaustion — "stop for good" for this page load.
   *  Never set on a gate-driven finish (blocked route, logout, session rotation): reconcile()
   *  must still be able to restart capture once the user is gated back in. Resets only on a
   *  fresh page load — there is no in-app reset, this is deliberate. */
  private disabled = false;
  /** Set when a buffer overflow drops everything but snapshot/meta events; cleared once the next
   *  successful chunk upload calls record.takeFullSnapshot() to re-anchor the replay. */
  private needsSnapshot = false;

  init(): void {
    this.auth.user$.subscribe(() => this.reconcile());
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      this.reconcile(e.urlAfterRedirects);
      this.emit('route', { path: templatePath(e.urlAfterRedirects) });
    });
    window.addEventListener('pagehide', () => this.finish(false, true));
  }

  /** Public and pure so the gate logic is unit-testable (the recorder itself is e2e-tested). */
  gatesPass(user: AuthUser | undefined, url: string): boolean {
    return !!(
      user?.uxReplay?.serverEnabled &&
      user.uxReplay.userEnabled &&
      !user.impersonated &&
      !BLOCKED_PREFIXES.some((p) => url === p || url.startsWith(p + '/') || url.startsWith(p + '?')) &&
      navigator.doNotTrack !== '1' &&
      typeof CompressionStream !== 'undefined'
    );
  }

  emit(name: string, payload: Record<string, string | number | boolean>): void {
    if (!this.active) return; // record.addCustomEvent throws when recording is stopped
    record.addCustomEvent(name, payload);
  }

  private reconcile(url: string = this.router.url): void {
    const should = this.gatesPass(this.auth.user, url);
    if (should && !this.active && !this.disabled) void this.start();
    if (!should && this.active) this.finish();
  }

  private async start(): Promise<void> {
    const user = this.auth.user;
    if (!user) return;
    // Reset FIRST, before the async POST below: a requeue() from the just-ended prior session can
    // still resolve after this point and must never bleed into the new session's chunk 0.
    this.buffer = [];
    this.bufferBytes = 0;
    const id = crypto.randomUUID(); // in memory only — nothing is written to the terminal's storage
    const res = await this.post(
      'api/ux/sessions',
      JSON.stringify({
        id,
        viewportW: window.innerWidth,
        viewportH: window.innerHeight,
        appVersion: gitVersion,
        companyId: null,
        uaFamily: uaFamily(),
        osFamily: osFamily(),
      }),
      'application/json'
    ).catch(() => undefined);
    if (!res?.ok) return;
    this.sessionId = id;
    this.seq = 0;
    this.failures = 0;
    this.startedAt = Date.now();
    this.active = true;
    this.zone.runOutsideAngular(() => {
      // Outside NgZone: zone.js patches MutationObserver — inside the zone every DOM mutation
      // would trigger a change-detection tick (design.md § 10 #8).
      // No cast: RecordOptions is now correctly bound to eventWithTime (see ux-masking.ts), so T
      // infers from the argument.
      this.stopFn = record(buildRecordOptions((e) => this.push(e)));
      this.timer = setInterval(() => void this.flush(), FLUSH_MS);
    });
  }

  private push(e: eventWithTime): void {
    const size = JSON.stringify(e).length;
    if (this.bufferBytes + size > BUFFER_LIMIT) {
      // Drop everything but snapshots/meta and flag needsSnapshot: flush()'s next successful
      // upload calls record.takeFullSnapshot() to re-anchor the replay before more is lost.
      this.buffer = this.buffer.filter((ev) => ev.type === EventType.FullSnapshot || ev.type === EventType.Meta);
      this.bufferBytes = this.buffer.reduce((n, ev) => n + JSON.stringify(ev).length, 0);
      this.needsSnapshot = true;
    }
    this.buffer.push(e);
    this.bufferBytes += size;
    if (this.bufferBytes >= FLUSH_BYTES) void this.flush();
  }

  /** terminal and viaKeepalive are independent: terminal means "run despite active already being
   *  false" (true for EVERY finish() reason — a gate flip, a blocked route, logout, 4h rotation,
   *  or pagehide all count); viaKeepalive means "use fetch's keepalive flag and the 60 KiB budget
   *  cap" and is true ONLY for the pagehide path. An ordinary finish leaves the tab open, so it
   *  flushes the full buffer with a normal (uncapped, non-keepalive) request — coupling the cap
   *  to every finish reason would silently drop a large tail buffer on nothing more than a
   *  blocked-route navigation. */
  private async flush(terminal = false, viaKeepalive = false): Promise<void> {
    if ((!terminal && !this.active) || this.flushing || this.buffer.length === 0) return;
    if (!terminal && Date.now() - this.startedAt > SESSION_MAX_MS) {
      this.finish(true); // rotate: fresh session id + snapshot once torn down, if still gated
      return;
    }
    this.flushing = true;
    const events = this.buffer;
    this.buffer = [];
    this.bufferBytes = 0;
    try {
      const body = await gzip(JSON.stringify(events));
      if (viaKeepalive && body.size > FINAL_CHUNK_LIMIT) return; // keepalive budget cannot carry it — accept the loss
      const res = await this.post(
        `api/ux/chunks`,
        body,
        'application/gzip',
        { 'X-Ux-Session': this.sessionId!, 'X-Ux-Seq': String(this.seq) },
        viaKeepalive
      );
      if (res.ok) {
        this.seq++;
        this.failures = 0;
        if (this.needsSnapshot && this.active) {
          record.takeFullSnapshot();
          this.needsSnapshot = false;
        }
      } else if (res.status >= 500) this.requeue(events);
      else {
        this.disabled = true; // 4xx: gates changed / quota — stop for good, discard
        this.teardown();
      }
    } catch {
      this.requeue(events);
    } finally {
      this.flushing = false;
    }
  }

  private requeue(events: eventWithTime[]): void {
    this.failures++;
    if (this.failures >= MAX_FAILURES) {
      this.disabled = true;
      this.teardown();
      return;
    }
    this.buffer = events.concat(this.buffer);
    this.bufferBytes = this.buffer.reduce((n, ev) => n + JSON.stringify(ev).length, 0);
  }

  /** rotate=true (the SESSION_MAX_MS case): once the async tail below has ended the old session,
   *  reconcile() re-evaluates the gates and — if the user is still gated in — starts a brand new
   *  session id, which itself begins with a fresh rrweb snapshot. No loop risk: reconcile() only
   *  calls start() when the gates pass, and disabled (set on a hard-stop reason, never here)
   *  still blocks it either way.
   *  viaKeepalive=true ONLY from the pagehide listener — see flush()'s doc comment; every other
   *  caller (reconcile()'s gate flip, the rotation branch below) leaves it false. */
  private finish(rotate = false, viaKeepalive = false): void {
    if (!this.active || this.finishing) return;
    this.finishing = true;
    this.stopCapture(); // synchronous: capture (and emit()) stop before any network round-trip
    void this.flush(true, viaKeepalive).then(() => {
      if (this.sessionId)
        void this.post(
          'api/ux/sessions/end',
          null,
          'application/json',
          { 'X-Ux-Session': this.sessionId },
          viaKeepalive
        ).catch(() => undefined);
      this.teardown();
      this.finishing = false;
      if (rotate) this.reconcile();
    });
  }

  /** Synchronous half of teardown, run FIRST from finish() — before the async flush/end
   *  round-trip — so a transition into a blocked route (or any gate flip) stops rrweb capture
   *  and turns emit() into a no-op immediately, not after the fetch resolves. */
  private stopCapture(): void {
    this.active = false;
    this.stopFn?.();
    this.stopFn = undefined;
    if (this.timer) clearInterval(this.timer);
  }

  private teardown(): void {
    this.stopCapture();
    this.buffer = [];
    this.bufferBytes = 0;
    this.sessionId = undefined;
    this.needsSnapshot = false;
  }

  private post(
    url: string,
    body: BodyInit | null,
    contentType: string,
    headers: Record<string, string> = {},
    keepalive = false
  ): Promise<Response> {
    // fetch, not HttpClient: keepalive support, and it must not trip the loader/logging interceptors.
    return fetch(url, {
      method: 'POST',
      body,
      keepalive,
      headers: { Authorization: `Bearer ${this.auth.user?.token}`, 'Content-Type': contentType, ...headers },
    });
  }
}

async function gzip(json: string): Promise<Blob> {
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).blob();
}

function uaFamily(): string {
  const ua = navigator.userAgent;
  return ua.includes('Firefox')
    ? 'Firefox'
    : ua.includes('Edg')
      ? 'Edge'
      : ua.includes('Chrome')
        ? 'Chrome'
        : ua.includes('Safari')
          ? 'Safari'
          : 'other';
}
function osFamily(): string {
  const ua = navigator.userAgent;
  return ua.includes('Windows') ? 'Windows' : ua.includes('Mac') ? 'macOS' : ua.includes('Linux') ? 'Linux' : 'other';
}
